import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Paperclip, Sparkles, Loader2, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

const STARTERS = [
  "What is an Encumbrance Certificate?",
  "Is agricultural land safe to buy in Maharashtra?",
  "What documents should I check before buying a plot?",
  "What is a 7/12 extract?",
];

export const LEAD_KEY = "tt_ai_lead";

export const LeadGate = ({ onReady, compact }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || phone.trim().length < 7) {
      toast.error("Please enter your name and a valid phone number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/lead", { name: name.trim(), phone: phone.trim() });
      const lead = { lead_id: data.lead_id, name: data.name };
      localStorage.setItem(LEAD_KEY, JSON.stringify(lead));
      onReady(lead);
    } catch {
      toast.error("Could not start the chat. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className={`flex flex-col justify-center h-full ${compact ? "px-5 py-6" : "px-8 py-12 max-w-md mx-auto"}`} data-testid="ai-lead-gate">
      <div className="w-11 h-11 bg-[#113835] text-[#F4D387] flex items-center justify-center mb-5">
        <Sparkles size={20} strokeWidth={1.5} />
      </div>
      <p className="font-heading text-lg font-medium text-[#113835]">Ask Terraa AI anything about property.</p>
      <p className="mt-2 text-sm text-gray-500 font-light">Tell us who you are and start chatting — free.</p>
      <form onSubmit={submit} className="mt-6 space-y-3">
        <input
          data-testid="ai-lead-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="tt-input w-full"
        />
        <input
          data-testid="ai-lead-phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone / WhatsApp number"
          type="tel"
          className="tt-input w-full"
        />
        <button data-testid="ai-lead-submit" type="submit" disabled={busy} className="tt-btn-gold w-full justify-center disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} strokeWidth={1.5} />} Start Chatting
        </button>
      </form>
      <p className="mt-3 text-[0.65rem] text-gray-400 font-light">We may contact you about your property questions. No spam.</p>
    </div>
  );
};

export const TerraaChat = ({ compact = false }) => {
  const [lead, setLead] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LEAD_KEY)) || null; } catch { return null; }
  });
  const [sessionId] = useState(() => {
    const k = "tt_ai_session";
    let s = localStorage.getItem(k);
    if (!s) { s = `web-${crypto.randomUUID()}`; localStorage.setItem(k, s); }
    return s;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [attached, setAttached] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const attachFile = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      setAttached({ id: data.id, name: data.filename });
      toast.success("Document attached — ask me anything about it");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const send = useCallback(async (text) => {
    const message = (text || input).trim();
    if (!message || streaming || !lead) return;
    const docIds = attached ? [attached.id] : [];
    setMessages((m) => [...m, { role: "user", content: message, doc: attached?.name }, { role: "assistant", content: "" }]);
    setInput("");
    setAttached(null);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id: lead.lead_id, session_id: sessionId, message, document_ids: docIds }),
      });
      if (!res.ok) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't answer that just now. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, lead, attached, sessionId]);

  if (!lead) return <LeadGate compact={compact} onReady={setLead} />;

  return (
    <div className="flex flex-col h-full" data-testid="terraa-chat">
      <div className={`flex-1 overflow-y-auto ${compact ? "px-4 py-4" : "px-6 md:px-8 py-6"} space-y-4`} data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="pt-2">
            <p className="text-sm text-gray-500 font-light mb-4">
              Hi {lead.name.split(" ")[0]} — I'm Terraa AI. Ask me about titles, ECs, 7/12 extracts, or attach a property
              document (Marathi/Hindi works too) and I'll read it for you.
            </p>
            <div className="flex flex-wrap gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  data-testid={`ai-starter-${STARTERS.indexOf(s)}`}
                  onClick={() => send(s)}
                  className="text-xs font-light text-[#1D504C] border border-[#113835]/20 px-3 py-2 hover:border-[#C1A74A] hover:text-[#113835] transition-colors text-left"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] px-4 py-3 text-sm font-light leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-[#113835] text-white" : "bg-[#F4F6F5] text-[#113835]"
              }`}
            >
              {m.doc && (
                <p className="flex items-center gap-1.5 text-[0.65rem] uppercase tracking-wide mb-1.5 opacity-70">
                  <FileText size={11} /> {m.doc}
                </p>
              )}
              {m.content || (streaming && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin" /> : "")}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className={`border-t border-[#113835]/10 ${compact ? "p-3" : "p-4 md:p-5"}`}>
        {attached && (
          <div className="flex items-center gap-2 mb-2 text-xs text-[#1D504C] bg-[#1D504C]/8 px-3 py-1.5 w-fit" data-testid="ai-attached-doc">
            <FileText size={12} /> {attached.name}
            <button onClick={() => setAttached(null)} aria-label="Remove attachment"><X size={12} /></button>
          </div>
        )}
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-center gap-2"
        >
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="hidden"
            onChange={(e) => { attachFile(e.target.files[0]); e.target.value = ""; }} />
          <button
            type="button"
            data-testid="ai-attach-btn"
            onClick={() => fileRef.current?.click()}
            className="shrink-0 w-10 h-10 border border-[#113835]/20 text-[#1D504C] hover:border-[#C1A74A] transition-colors flex items-center justify-center"
            aria-label="Attach a document"
          >
            <Paperclip size={16} strokeWidth={1.5} />
          </button>
          <input
            data-testid="ai-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about a property, or attach a document…"
            className="flex-1 border border-[#113835]/20 px-4 py-2.5 text-sm font-light text-[#113835] focus:outline-none focus:border-[#C1A74A]"
          />
          <button
            type="submit"
            data-testid="ai-chat-send"
            disabled={streaming || !input.trim()}
            className="shrink-0 w-10 h-10 bg-[#113835] text-[#F4D387] hover:bg-[#16324F] transition-colors flex items-center justify-center disabled:opacity-50"
            aria-label="Send message"
          >
            {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} strokeWidth={1.5} />}
          </button>
        </form>
        <p className="mt-2 text-[0.6rem] text-gray-400 font-light">
          Terraa AI gives general guidance, not legal advice. For real record checks, request a verification.
        </p>
      </div>
    </div>
  );
};
